import { cn } from '@/lib/utils';
import cytoscape from 'cytoscape';
import {
	Maximize2,
	Minimize2,
	MinusCircle,
	MousePointer2,
	PlusCircle,
	ScanSearch,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface TrustGraphCanvasProps {
	readonly elements: readonly cytoscape.ElementDefinition[];
	readonly selectedNodeId: string | null;
	readonly onSelectNode: (nodeId: string) => void;
	readonly scope: 'org' | 'repo';
	readonly compact?: boolean;
}

export function TrustGraphCanvas({
	elements,
	selectedNodeId,
	onSelectNode,
	scope,
	compact = false,
}: TrustGraphCanvasProps) {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const cyRef = useRef<cytoscape.Core | null>(null);
	const onSelectRef = useRef(onSelectNode);
	onSelectRef.current = onSelectNode;
	const [renderFallback, setRenderFallback] = useState(false);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [boxSelectionMode, setBoxSelectionMode] = useState(false);
	const stableOnSelect = useCallback((nodeId: string) => onSelectRef.current(nodeId), []);
	const fallbackNodes = useMemo(
		() =>
			elements
				.filter((element) => element.group === 'nodes')
				.map((element) => {
					const data = element.data as Record<string, unknown>;
					return {
						id: String(data.id ?? ''),
						label: String(data.label ?? data.id ?? ''),
						risk: String(data.risk ?? 'none'),
					};
				}),
		[elements],
	);

	useEffect(() => {
		if (!(containerRef.current && supportsCanvas())) {
			setRenderFallback(true);
			return;
		}

		let cy: cytoscape.Core;
		try {
			cy = cytoscape({
				container: containerRef.current,
				elements: [],
				style: getStylesheet(),
				userZoomingEnabled: true,
				boxSelectionEnabled: true,
				selectionType: 'single',
			});
		} catch {
			setRenderFallback(true);
			return;
		}

		cy.on('tap', 'node', (event) => {
			stableOnSelect(String(event.target.id()));
		});

		cyRef.current = cy;
		return () => {
			cy.destroy();
			cyRef.current = null;
		};
	}, [stableOnSelect]);

	useEffect(() => {
		const cy = cyRef.current;
		if (!cy || renderFallback) {
			return;
		}

		cy.batch(() => {
			cy.elements().remove();
			cy.add([...elements]);
		});

		cy.layout(getLayout(scope, compact)).run();
		cy.fit(cy.elements(), compact ? 24 : 40);
	}, [compact, elements, renderFallback, scope]);

	useEffect(() => {
		const cy = cyRef.current;
		if (!(cy && selectedNodeId) || renderFallback) {
			return;
		}

		cy.elements().removeClass('is-selected is-neighbor');
		const target = cy.getElementById(selectedNodeId);
		if (target.length > 0) {
			target.addClass('is-selected');
			const neighborhood = target.closedNeighborhood();
			neighborhood.addClass('is-neighbor');
			cy.animate({ fit: { eles: neighborhood, padding: compact ? 30 : 70 } }, { duration: 220 });
		}
	}, [compact, renderFallback, selectedNodeId]);

	useEffect(() => {
		const cy = cyRef.current;
		if (!cy || renderFallback) return;
		cy.userPanningEnabled(!boxSelectionMode);
		cy.boxSelectionEnabled(boxSelectionMode);
	}, [boxSelectionMode, renderFallback]);

	useEffect(() => {
		if (!isFullscreen) return;
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setIsFullscreen(false);
		};
		document.addEventListener('keydown', handleKey);
		return () => document.removeEventListener('keydown', handleKey);
	}, [isFullscreen]);

	const handleZoomIn = useCallback(() => {
		const cy = cyRef.current;
		if (cy)
			cy.zoom({
				level: cy.zoom() * 1.3,
				renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 },
			});
	}, []);

	const handleZoomOut = useCallback(() => {
		const cy = cyRef.current;
		if (cy)
			cy.zoom({
				level: cy.zoom() / 1.3,
				renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 },
			});
	}, []);

	const toggleFullscreen = useCallback(() => {
		setIsFullscreen((v) => {
			const next = !v;
			// Schedule a resize after the DOM updates from the fullscreen toggle
			requestAnimationFrame(() => {
				const cy = cyRef.current;
				if (cy) {
					cy.resize();
					cy.fit(cy.elements(), 50);
				}
			});
			return next;
		});
	}, []);

	const handleFitAll = useCallback(() => {
		const cy = cyRef.current;
		if (cy) cy.animate({ fit: { eles: cy.elements(), padding: 50 } }, { duration: 200 });
	}, []);

	if (renderFallback) {
		return (
			<div
				className={cn(
					'grid gap-2 rounded-2xl border border-border/70 bg-muted/20 p-3',
					compact ? 'min-h-40 grid-cols-2' : 'min-h-56 sm:grid-cols-2 xl:grid-cols-3',
				)}
			>
				{fallbackNodes.map((node) => (
					<button
						key={node.id}
						type="button"
						onClick={() => onSelectNode(node.id)}
						className={cn(
							'rounded-xl border px-3 py-2 text-left text-sm transition-colors',
							node.id === selectedNodeId
								? 'border-primary/40 bg-primary/10 text-primary'
								: 'border-border bg-background text-foreground hover:border-primary/20',
						)}
					>
						<div className="font-medium">{node.label}</div>
						<div className="mt-1 text-xs uppercase tracking-[0.16em] text-muted-foreground">
							{scope} · {node.risk}
						</div>
					</button>
				))}
			</div>
		);
	}

	const canvasEl = (
		<div className="relative">
			<div
				ref={containerRef}
				className={cn(
					'w-full rounded-2xl border border-border/70 bg-[linear-gradient(180deg,rgba(99,102,241,0.03),rgba(255,255,255,0.98))]',
					isFullscreen ? 'h-[calc(100vh-4rem)]' : compact ? 'h-64' : 'h-[32rem]',
				)}
			/>
			<div
				className={cn(
					'absolute right-3 top-3 flex flex-col gap-1.5',
					compact && !isFullscreen && 'right-2 top-2',
				)}
			>
				<CanvasButton
					title="Zoom in"
					onClick={handleZoomIn}
					icon={<PlusCircle className="h-4 w-4" />}
				/>
				<CanvasButton
					title="Zoom out"
					onClick={handleZoomOut}
					icon={<MinusCircle className="h-4 w-4" />}
				/>
				<CanvasButton
					title="Fit all nodes"
					onClick={handleFitAll}
					icon={<ScanSearch className="h-4 w-4" />}
				/>
				<CanvasButton
					title={boxSelectionMode ? 'Pan mode' : 'Box select mode'}
					onClick={() => setBoxSelectionMode((v) => !v)}
					active={boxSelectionMode}
					icon={<MousePointer2 className="h-4 w-4" />}
				/>
				<CanvasButton
					title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
					onClick={toggleFullscreen}
					icon={
						isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />
					}
				/>
			</div>
		</div>
	);

	if (isFullscreen) {
		return (
			<div className="fixed inset-0 z-50 bg-background/95 p-6 backdrop-blur-sm">
				<div className="flex h-full flex-col gap-3">
					<div className="flex items-center justify-between">
						<span className="text-sm font-medium text-foreground">Trust Graph — Fullscreen</span>
						<button
							type="button"
							onClick={toggleFullscreen}
							className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted"
						>
							Exit fullscreen
						</button>
					</div>
					<div className="min-h-0 flex-1">{canvasEl}</div>
				</div>
			</div>
		);
	}

	return canvasEl;
}

function CanvasButton({
	title,
	onClick,
	icon,
	active = false,
}: {
	title: string;
	onClick: () => void;
	icon: React.ReactNode;
	active?: boolean;
}) {
	return (
		<button
			type="button"
			title={title}
			onClick={onClick}
			className={cn(
				'rounded-lg border p-1.5 shadow-sm transition-colors',
				active
					? 'border-primary/40 bg-primary/10 text-primary'
					: 'border-border/80 bg-card/90 text-muted-foreground hover:bg-card hover:text-foreground',
			)}
		>
			{icon}
		</button>
	);
}

function supportsCanvas(): boolean {
	if (typeof HTMLCanvasElement === 'undefined') {
		return false;
	}
	if (typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('jsdom')) {
		return false;
	}
	try {
		const canvas = document.createElement('canvas');
		return typeof canvas.getContext === 'function' && canvas.getContext('2d') !== null;
	} catch {
		return false;
	}
}

function getLayout(scope: 'org' | 'repo', compact: boolean): cytoscape.LayoutOptions {
	if (scope === 'repo') {
		return {
			name: 'breadthfirst',
			directed: true,
			padding: compact ? 30 : 60,
			spacingFactor: compact ? 1.6 : 2.0,
		};
	}
	return {
		name: 'cose',
		animate: false,
		padding: compact ? 30 : 60,
		nodeRepulsion: compact ? 80000 : 160000,
		idealEdgeLength: compact ? 140 : 220,
		nodeOverlap: 60,
		gravity: 0.08,
		numIter: 800,
	};
}

function getStylesheet() {
	return [
		{
			selector: 'node',
			style: {
				width: 20,
				height: 20,
				label: 'data(label)',
				'font-size': 9,
				'font-weight': 500,
				color: '#94a3b8',
				'background-color': '#cbd5e1',
				'text-wrap': 'ellipsis',
				'text-max-width': 90,
				'text-valign': 'bottom',
				'text-margin-y': 5,
				'text-outline-color': '#ffffff',
				'text-outline-width': 2.5,
				'text-outline-opacity': 1,
				'border-width': 1.5,
				'border-color': '#ffffff',
			},
		},
		{
			selector: 'node.is-selected',
			style: {
				color: '#0f172a',
				'font-weight': 700,
				'font-size': 10,
				width: 26,
				height: 26,
				'overlay-color': '#6366f1',
				'overlay-opacity': 0.14,
				'overlay-padding': 10,
			},
		},
		{
			selector: 'node.is-neighbor',
			style: {
				color: '#1e293b',
				'font-weight': 600,
				'font-size': 9,
			},
		},
		{
			selector: 'node[category = "control-plane"]',
			style: { 'background-color': '#6366f1', shape: 'roundrectangle' },
		},
		{
			selector: 'node[category = "automation"]',
			style: { 'background-color': '#3b82f6', shape: 'ellipse' },
		},
		{
			selector: 'node[category = "execution"]',
			style: { 'background-color': '#f59e0b', shape: 'diamond' },
		},
		{
			selector: 'node[category = "environment"]',
			style: { 'background-color': '#22c55e', shape: 'hexagon' },
		},
		{
			selector: 'node[category = "supply-chain"]',
			style: { 'background-color': '#475569', shape: 'ellipse' },
		},
		{
			selector: 'node[category = "evidence"]',
			style: { 'background-color': '#eab308', shape: 'round-tag' },
		},
		{
			selector: 'node[category = "authority"]',
			style: { 'background-color': '#64748b', shape: 'pentagon' },
		},
		{
			selector: 'node[risk = "high"]',
			style: { 'border-color': '#ea580c', 'border-width': 3 },
		},
		{
			selector: 'node[risk = "critical"]',
			style: { 'background-color': '#dc2626', 'border-color': '#7f1d1d', 'border-width': 4 },
		},
		{
			selector: 'edge.is-neighbor',
			style: {
				label: 'data(label)',
				'font-size': 8,
				color: '#475569',
				'text-background-color': '#ffffff',
				'text-background-opacity': 0.92,
				'text-background-padding': 2,
				opacity: 1,
				width: 2,
			},
		},
		{
			selector: 'edge',
			style: {
				width: 1.5,
				'curve-style': 'bezier',
				'target-arrow-shape': 'triangle',
				'arrow-scale': 0.65,
				'line-color': '#d1d5db',
				'target-arrow-color': '#d1d5db',
				label: '',
				opacity: 0.7,
			},
		},
		{
			selector: 'edge[risk = "high"]',
			style: { 'line-color': '#ea580c', 'target-arrow-color': '#ea580c', width: 2, opacity: 1 },
		},
		{
			selector: 'edge[risk = "critical"]',
			style: { 'line-color': '#dc2626', 'target-arrow-color': '#dc2626', width: 2.5, opacity: 1 },
		},
	] as cytoscape.StylesheetJson;
}
